import { useMemo } from 'react';

import {
  getGenericLine,
  getLineCategory,
  hasBeneficiaries,
  hasMedical,
  hasProperty,
  hasRisk
} from '@utils/helper-utils';

import {
  bankersValidationSchema,
  beneficiariesValidationSchema,
  costChargesValidationSchema,
  coversValidationSchema,
  descriptionOfRiskValidationSchema,
  expatValidationSchema,
  groupMedicalValidationSchema,
  insuredExpatValidationSchema,
  insuredsValidationSchema,
  marineHullValidationSchema,
  marineValidationSchema,
  medicalInsuredsValidationSchema,
  medicalValidationSchema,
  motorValidationSchema,
  payerOwnerValidationSchema,
  policyDetailsValidationSchema,
  propertyValidationSchema,
  travelValidationSchema
} from '../validations';

import { GenericLines, LineCategory } from 'src/enums';

export function useProposalValidation(
  status: string,
  lineCode: string,
  beneficiaryMaxNumber: number,
  policyType: 'CERTIFICATE' | 'GROUP' | 'INDIVIDUAL'
) {
  return useMemo(() => {
    const lineCategory = getLineCategory(lineCode);
    const isCertificate = policyType === 'CERTIFICATE';

    let base = hasMedical(lineCode)
      ? costChargesValidationSchema()
      : coversValidationSchema(
          lineCode,
          (row: any) =>
            row?.isEditable === true &&
            !['CLOSED_WON', 'CLOSED_LOST'].includes(status)
        ).concat(costChargesValidationSchema());

    base = base.concat(policyDetailsValidationSchema());

    if (!isCertificate) {
      base = base.concat(payerOwnerValidationSchema());
      if (!hasMedical(lineCode)) {
        base = base.concat(insuredsValidationSchema(lineCode));
      }
    }

    const beneficiariesSchemaIfShown =
      !isCertificate && hasBeneficiaries(lineCode)
        ? beneficiariesValidationSchema(beneficiaryMaxNumber)
        : null;

    if (lineCategory === LineCategory.Expat) {
      let schema = base
        .concat(insuredExpatValidationSchema())
        .concat(expatValidationSchema());

      if (beneficiariesSchemaIfShown) {
        schema = schema.concat(beneficiariesSchemaIfShown);
      }

      return schema;
    }

    if (lineCategory === LineCategory.Motor) {
      let schema = base;

      if (policyType !== 'GROUP') {
        schema = schema.concat(motorValidationSchema(lineCode));
      }

      if (beneficiariesSchemaIfShown) {
        schema = schema.concat(beneficiariesSchemaIfShown);
      }

      return schema;
    }

    if (lineCategory === LineCategory.Medical) {
      let schema = base
        .concat(medicalValidationSchema())
        .concat(medicalInsuredsValidationSchema());

      if (beneficiariesSchemaIfShown) {
        schema = schema.concat(beneficiariesSchemaIfShown);
      }

      return schema;
    }

    if (getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP) {
      let schema = base.concat(groupMedicalValidationSchema());

      if (beneficiariesSchemaIfShown) {
        schema = schema.concat(beneficiariesSchemaIfShown);
      }

      return schema;
    }

    if (lineCategory === LineCategory.Travel) {
      let schema = base
        .concat(travelValidationSchema())
        .concat(medicalInsuredsValidationSchema(true));

      if (beneficiariesSchemaIfShown) {
        schema = schema.concat(beneficiariesSchemaIfShown);
      }

      return schema;
    }

    if (getGenericLine(lineCode) === GenericLines.MARINE_HULL) {
      return base.concat(marineHullValidationSchema());
    }

    if (
      getGenericLine(lineCode) === GenericLines.MARINE &&
      policyType !== 'GROUP'
    ) {
      return base.concat(marineValidationSchema());
    }

    if (getGenericLine(lineCode) === GenericLines.BBB) {
      return base.concat(bankersValidationSchema());
    }

    if (hasProperty(lineCode)) {
      return base.concat(propertyValidationSchema());
    }

    if (hasRisk(lineCode)) {
      return base.concat(descriptionOfRiskValidationSchema());
    }

    if (beneficiariesSchemaIfShown) {
      return base.concat(beneficiariesSchemaIfShown);
    }

    return base;
  }, [lineCode, status, beneficiaryMaxNumber, policyType]);
}
